import React, { useState, useEffect } from 'react';
import { Search, Mail, User, Trash2, Edit, BookOpen } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { 
        page, 
        limit: 20, 
        search: search || undefined,
        isActive: statusFilter || undefined,
      };
      const response = await api.get('/admin/users', { params });
      if (response.data.success) {
        setUsers(response.data.users);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return 'Inactive';
      case 1:
        return 'Active';
      case 2:
        return 'Comp';
      default:
        return 'Active';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return 'bg-gray-100 text-gray-600';
      case 1:
        return 'bg-green-100 text-green-700';
      case 2:
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  const openEditModal = (user) => {
    // Ensure is_active is properly normalized as integer: 0, 1, or 2
    const normalizedIsActive = user.is_active !== undefined && user.is_active !== null 
      ? parseInt(user.is_active) 
      : 1;
    
    setSelectedUser({
      ...user,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      is_active: normalizedIsActive, // Ensure it's 0, 1, or 2 (integer)
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      setSaving(true);
      // Ensure is_active is properly parsed: 0 (Inactive), 1 (Active), or 2 (Comp)
      const isActiveValue = selectedUser.is_active !== undefined && selectedUser.is_active !== null 
        ? parseInt(selectedUser.is_active) 
        : 1;
      
      // Validate the value is 0, 1, or 2
      if (![0, 1, 2].includes(isActiveValue)) {
        toast.error('Invalid status value. Must be Active (1), Inactive (0), or Comp (2).');
        setSaving(false);
        return;
      }

      const response = await api.put(`/admin/users/${selectedUser.id}`, {
        firstName: selectedUser.first_name?.trim(),
        lastName: selectedUser.last_name?.trim(),
        email: selectedUser.email?.trim(),
        isActive: isActiveValue, // Send 0, 1, or 2
      });
      if (response.data.success) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (user) => {
    setDeleteTarget(user);
    setShowConfirmModal(true);
  };

  const handleViewStories = (user) => {
    const params = new URLSearchParams();
    params.set('user', user.id);
    if (user.first_name) {
      params.set('name', user.first_name);
    }
    navigate(`/admin/stories?${params.toString()}`);
  };

  const handleDelete = async (userId) => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        toast.success('User deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminSidebar>
      <div className="mb-6 sm:mb-8">
        <p className="text-sm sm:text-base text-gray-600">Manage all platform users</p>
      </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search users by email or name..."
                className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all bg-white"
              >
                <option value="">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
                <option value="2">Comp</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={<User className="h-7 w-7" />}
            title={
              search || statusFilter
                ? `No users found${search ? ` for "${search}"` : ''}${search && statusFilter ? ' with' : ''}${statusFilter ? ` status: ${getStatusLabel(parseInt(statusFilter))}` : ''}`
                : 'No users yet'
            }
            description={
              search || statusFilter
                ? 'Try adjusting your search terms or clearing the filters to see all users.'
                : 'When new parents join MyStoryMe, their accounts will appear here for you to manage.'
            }
            action={
              search || statusFilter
                ? {
                    label: 'Clear filters',
                    onClick: () => {
                      setSearch('');
                      setStatusFilter('');
                      setPage(1);
                    },
                  }
                : undefined
            }
          />
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Profiles</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Stories</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Joined</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 transition-all duration-150 group">
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md ring-2 ring-white">
                                {user.first_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                              <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 flex items-center truncate">
                                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{user.email}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 text-sm font-medium">
                            {user.profile_count || 0}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-pink-100 text-pink-700 text-sm font-medium">
                            {user.story_count || 0}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                          <div className="text-sm text-gray-600">
                            {new Date(user.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(user.is_active)}`}>
                            <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-current"></span>
                            {getStatusLabel(user.is_active)}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewStories(user)}
                              className="p-2 text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="View Stories"
                            >
                              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                              onClick={() => openEditModal(user)}
                              className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(user)}
                              className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <Modal title="Edit User" onClose={() => setShowEditModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={selectedUser.first_name}
                onChange={(e) =>
                  setSelectedUser((prev) => ({ ...prev, first_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={selectedUser.last_name}
                onChange={(e) =>
                  setSelectedUser((prev) => ({ ...prev, last_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={selectedUser.email}
                onChange={(e) =>
                  setSelectedUser((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedUser.is_active !== undefined && selectedUser.is_active !== null 
                  ? parseInt(selectedUser.is_active) 
                  : 1}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  if ([0, 1, 2].includes(newValue)) {
                    setSelectedUser((prev) => ({ ...prev, is_active: newValue }));
                  }
                }}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
                <option value={2}>Comp</option>
              </select>
              {selectedUser.is_active === 2 && (
                <p className="mt-1 text-xs text-purple-600">
                  Comp users receive complimentary access to the highest plan
                </p>
              )}
              {selectedUser.is_active === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  Inactive users cannot login. They will see a warning to contact support.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete User"
          description={`Are you sure you want to delete ${deleteTarget.first_name || 'this user'}? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={() => {
            setShowConfirmModal(false);
            handleDelete(deleteTarget.id);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminUsers;

